'use-client';
import * as React from 'react';
import classNames from 'clsx';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import type {
  IStylableButtonProps,
  IStylableButtonImperativeActions,
} from '../../../StylableButton.types';
import stylableButtonSemanticClassNames from '../../../StylableButton.semanticClassNames';
import ButtonComponent from './Button';
import styles from './Button.module.scss';
import extendedStyles from './extendedStyleApi.module.scss';

const TRANSITION_DURATION = 400;

type ButtonSkinProps = IStylableButtonProps & {
  extendedStyleApi?: boolean;
  labelClass?: string;
  iconClass?: string;
};

const ButtonSkin: React.ForwardRefRenderFunction<
  IStylableButtonImperativeActions,
  ButtonSkinProps
> = (props, ref) => {
  const {
    className,
    isDisabled,
    direction,
    extendedStyleApi = true,
    iconClass = '',
    labelClass = '',
    stylableButtonClassName,
    customClassNames = [],
    isMaxContent = false,
    isWrapText = false,
    fallbackDirectionClassName,
  } = props;

  const [active, setActive] = React.useState(false);
  const animationTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const semanticClassNames =
    props.semanticClassNames || stylableButtonSemanticClassNames;

  const handleMouseEnter = React.useCallback(() => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }

    setActive(true);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    // Set timeout to remove animations after 0.4s (matching CSS transition duration)
    animationTimeoutRef.current = setTimeout(() => {
      setActive(false);
    }, TRANSITION_DURATION);
  }, []);

  React.useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const rootClassName = classNames(
    styles.root,
    className,
    stylableButtonClassName,
    !direction && fallbackDirectionClassName,
    formatClassNames(semanticClassNames.root, ...customClassNames),
    {
      disabled: isDisabled,
      isMaxContent,
      isWrapText,
      [extendedStyles.root]: extendedStyleApi,
      [styles.active]: active,
    },
  );

  const labelClassName = classNames(
    labelClass,
    extendedStyleApi && extendedStyles.label,
    styles.label,
    formatClassNames(semanticClassNames.buttonLabel),
  );

  const iconClassName = classNames(
    iconClass,
    extendedStyleApi && extendedStyles.icon,
    styles.icon,
    formatClassNames(semanticClassNames.buttonIcon),
  );

  const linkClassName = styles.link;

  return (
    <ButtonComponent
      {...props}
      ref={ref}
      rootClassName={rootClassName}
      labelClassName={labelClassName}
      iconClassName={iconClassName}
      linkClassName={linkClassName}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  );
};

export default React.forwardRef(ButtonSkin);
